import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { QUERY_KEYS } from '@api'
import { Trailer, Truck, Vehicles } from '@types'

import { CarrierVehiclesApiFilters, getCarrierTrailers, getCarrierTrucks } from './api'

export type CarrierVehicleKindFilter = 'truck' | 'trailer'

export interface UseCarrierVehiclesParams {
  carrierId?: number
  equipmentIds?: number[]
  vehicleKind?: CarrierVehicleKindFilter
  statusFilter?: boolean
  enabled?: boolean
}

const CARRIER_VEHICLES_LIMIT = 500

const toApiFilters = (
  carrierId: number,
  equipmentIds: number[],
  statusFilter?: boolean,
): CarrierVehiclesApiFilters => {
  const filters: CarrierVehiclesApiFilters = {
    carrierId: [carrierId],
    limit: CARRIER_VEHICLES_LIMIT,
  }
  if (equipmentIds.length > 0) {
    filters.equipmentId = equipmentIds
  }
  if (statusFilter !== undefined) {
    filters.disabled = !statusFilter
  }
  return filters
}

const mapTruck = (truck: Truck): Truck & { enabled: boolean } => ({
  ...truck,
  enabled: truck.disableAt === null,
})

const mapTrailer = (trailer: Trailer): Trailer & { enabled: boolean } => ({
  ...trailer,
  enabled: trailer.disableAt === null,
})

const useCarrierVehicles = ({
  carrierId,
  equipmentIds = [],
  vehicleKind,
  statusFilter,
  enabled = true,
}: UseCarrierVehiclesParams) => {
  const fetchTrucks = vehicleKind !== 'trailer'
  const fetchTrailers = vehicleKind !== 'truck'
  const apiFilters = carrierId ? toApiFilters(carrierId, equipmentIds, statusFilter) : null
  const queryEnabled = enabled && !!apiFilters

  const trucksQuery = useQuery(
    [QUERY_KEYS.CARRIER_TRUCKS, apiFilters, vehicleKind],
    () => getCarrierTrucks(apiFilters!),
    { enabled: queryEnabled && fetchTrucks },
  )

  const trailersQuery = useQuery(
    [QUERY_KEYS.CARRIER_TRAILERS, apiFilters, vehicleKind],
    () => getCarrierTrailers(apiFilters!),
    { enabled: queryEnabled && fetchTrailers },
  )

  const trucks = useMemo(() => {
    if (!fetchTrucks) return []
    return trucksQuery.data?.trucks?.map(mapTruck) ?? []
  }, [fetchTrucks, trucksQuery.data])

  const trailers = useMemo(() => {
    if (!fetchTrailers) return []
    return trailersQuery.data?.trailers?.map(mapTrailer) ?? []
  }, [fetchTrailers, trailersQuery.data])

  const vehiclesData: Vehicles = useMemo(() => [...trucks, ...trailers], [trucks, trailers])

  return {
    vehiclesData,
    trucks,
    trailers,
    isLoading: (fetchTrucks && trucksQuery.isLoading) || (fetchTrailers && trailersQuery.isLoading),
    isFetching:
      (fetchTrucks && trucksQuery.isFetching) || (fetchTrailers && trailersQuery.isFetching),
  }
}

export { useCarrierVehicles }
